/**
 * The application shell: a single top bar over a full-width content region.
 *
 * Navigation lives in a dropdown at the top-left rather than a permanent left
 * rail. The rail cost ~240px on every screen to show links the operator reaches
 * a handful of times a session; the cockpit wants that width for terminals and
 * telemetry. The menu is still grouped by what an operator is doing — the live
 * cockpit first, the pools (configuration, consulted occasionally) next, the
 * system record and knobs last — so the order that justified the rail survives
 * the move.
 */

import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  ChevronDown,
  Coffee,
  Cpu,
  FolderGit2,
  FolderSymlink,
  Gauge,
  Hand,
  KeyRound,
  Keyboard,
  Languages,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  LogOut,
  Maximize,
  Minimize,
  MonitorDown,
  Moon,
  PlugZap,
  RefreshCw,
  Rows3,
  ScrollText,
  Server,
  Settings,
  Table,
  TriangleAlert,
  UserRound,
  Terminal as TerminalIcon,
  SquareTerminal,
  Sun,
  type LucideIcon,
} from 'lucide-react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { ShortcutsOverlay } from '@/terminal/shortcuts'
import { api } from '@/api/client'
import { useT, useLocale } from '@/i18n/context'
import { StatusDot, Spinner } from '@/components/primitives'
import { Icon } from '@/components/icon'
import { Logo } from '@/components/Logo'
import { ClassificationBanner, UtcClock } from '@/components/ClassificationBanner'
import { TacticalBackdrop } from '@/components/TacticalBackdrop'
import { useSystemStatus } from '@/hooks/useSystemStatus'
import { useTheme } from '@/hooks/useTheme'
import { useFullscreen } from '@/hooks/useFullscreen'
import { useDensity, toggleTight } from '@/hooks/useDensity'
import { InstallOffer, useInstallOffer } from '@/components/InstallOffer'
import { useCoarsePointer } from '@/hooks/usePointerKind'
import { useWakeLock } from '@/hooks/useWakeLock'
import { useImmersive } from '@/hooks/useImmersive'
import { useOverview, type OverviewData } from '@/routes/fleet-telemetry'
import { usageSeverityTone } from '@/lib/tone'
import type { UsageWindow } from '@/components/AccountUsage'
import type { ConnectionState } from '@/realtime/events'
import './Shell.css'

type NavLabelKey = Parameters<ReturnType<typeof useT>>[0]

interface NavItem {
  to: string
  labelKey: NavLabelKey
  icon: LucideIcon
  /** Renders a live count beside the item. */
  badge?: 'running'
}

interface NavGroup {
  labelKey: NavLabelKey
  items: NavItem[]
}

// Grouped by what an operator is doing, not by data model: the live sessions are
// the daily surface and lead; the pools are configuration consulted occasionally;
// the system group is the record and the knobs. Each destination carries a real
// icon rather than a stand-in glyph.
const NAV: NavGroup[] = [
  {
    labelKey: 'nav.liveOps',
    items: [
      // First, because it is where a day starts: what happened, what is waiting,
      // what to do next. The cockpit — the terminals — is the next stop.
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      // The work itself — missions and their tasks — between the summary and
      // the terminals that carry it out.
      { to: '/tasks', labelKey: 'nav.missions', icon: ListChecks },
      { to: '/live', labelKey: 'nav.cockpit', icon: SquareTerminal, badge: 'running' },
      // A separate destination because it answers a separate question: the cockpit
      // is what the workforce is doing, this is what is happening on a box.
      { to: '/terminals', labelKey: 'nav.terminals', icon: TerminalIcon },
      // And a third: moving bytes between the operator's own machine and a
      // fleet host. It was briefly a panel inside the cockpit and that was
      // wrong — the cockpit is the densest screen here, and a four-gigabyte
      // transfer is not a thing to do in a strip under a wall of terminals.
      { to: '/files', labelKey: 'nav.files', icon: FolderSymlink },
    ],
  },
  {
    labelKey: 'nav.pools',
    items: [
      { to: '/accounts', labelKey: 'nav.accounts', icon: KeyRound },
      { to: '/models', labelKey: 'nav.models', icon: Cpu },
      { to: '/hosts', labelKey: 'nav.hosts', icon: Server },
      { to: '/projects', labelKey: 'nav.projects', icon: FolderGit2 },
    ],
  },
  {
    labelKey: 'nav.system',
    items: [
      { to: '/audit', labelKey: 'nav.audit', icon: ScrollText },
      { to: '/settings', labelKey: 'nav.settings', icon: Settings },
    ],
  },
]

export function Shell() {
  const location = useLocation()
  const status = useSystemStatus()
  const immersive = useImmersive()
  // Hoisted here rather than left inside the top bar, because there are now
  // two controls for one offer — the bar's glyph and the sheet — and two
  // copies of `useInstallApp` would be two `beforeinstallprompt` listeners
  // racing to `preventDefault()` the same single-use event.
  const offer = useInstallOffer()

  // Moving focus to the main region on navigation is what makes the console
  // usable by keyboard: without it, every route change dumps focus back at the
  // top of the document and the operator re-tabs through the whole rail.
  //
  // Not on the first render, though. A cold load has not navigated anywhere —
  // the browser has already put the operator at the top of the document, which
  // is where the skip link is, and stealing focus into the content from there
  // does two harmful things at once: it skips the skip link (so the first Tab
  // leaves the document entirely while the content is still skeletons), and it
  // interrupts the screen reader's own announcement of the page it just loaded.
  // Keyed on the previous path rather than on a "have I mounted" flag: React's
  // StrictMode mounts, unmounts and remounts in development, which runs this
  // effect twice and would defeat a boolean guard — the second run would fire on
  // what is still the first render. Comparing paths cannot be fooled that way,
  // because the path has not changed either time.
  const lastPath = useRef<string | null>(null)
  useEffect(() => {
    const previous = lastPath.current
    lastPath.current = location.pathname
    if (previous === null || previous === location.pathname) return
    const main = document.getElementById('main')
    // A focus-driven navigation must not undo the focus that caused it. The live
    // cockpit routes on focusin from a terminal slot, and xterm's helper textarea
    // is already inside #main by the time this runs — so restoring focus here
    // blanked the very click that selected the pane, and the operator's first
    // keystroke went nowhere.
    //
    // Rejected alternative: dropping this focus call. It is the keyboard-
    // navigation affordance described above, and a navigation from the rail
    // leaves focus on a topbar control — outside #main — so the restore still
    // fires exactly where it was written to.
    if (!main || main.contains(document.activeElement)) return
    main.focus({ preventScroll: true })
  }, [location.pathname])

  return (
    <div className="k-app">
      <TacticalBackdrop />
      {/* Thirty pixels of letterhead, and not when the operator has asked for
          the screen. In an installed window the title bar directly above it
          prints the same two strings — the wordmark and the tagline, in that
          order — so the band is the second copy; in fullscreen it is a row of
          terminal spent on a name. The clock it carries is the one thing here
          that is a fact rather than an identity, so it moves into the top bar
          instead of going with it. */}
      {!immersive && <ClassificationBanner />}
      <div className="k-shell">
        <div className="k-shell__main">
          <TopBar connection={status.connection} immersive={immersive} offer={offer} />
          <main id="main" className="k-content" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
      {/* The last row of the frame, below the shell rather than over it. It is
          shown once and dismissed for good, so the cost is a hundred pixels on
          one visit — and on none of them does it cover the thing the operator
          opened the console to do. It sat over the terminal's key bar as a
          float, which is how that was found. */}
      <InstallOffer
        open={offer.open}
        onDismiss={offer.hide}
        state={offer.state}
        install={offer.install}
      />
    </div>
  )
}

/* --------------------------------------------------------------- NavMenu */

/**
 * The navigation, as a dropdown at the top-left. The trigger doubles as a
 * "where am I" indicator: it carries the brand mark and the label of the section
 * currently open, so the header says the same thing the old rail's active item
 * did, in a fraction of the width.
 */
function NavMenu() {
  const t = useT()
  const status = useSystemStatus()
  const location = useLocation()
  const current = NAV.flatMap((g) => g.items).find(
    (i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'),
  )

  return (
    // A <nav> landmark, wrapping the trigger as well as the panel. The rail this
    // replaced was one; the dropdown that took its place was a bare div of
    // links, so the console had no navigation landmark at all — nothing for a
    // screen-reader user to jump to, and nothing named. It wraps the *whole*
    // disclosure rather than the panel because the panel exists only while the
    // menu is open, and a landmark that appears on demand is not one.
    <Popover as="nav" className="k-navmenu" aria-label={t('nav.menu')}>
      <PopoverButton className="k-navmenu__button">
        <Icon icon={LayoutGrid} size={15} />
        <span className="k-navmenu__current">{current ? t(current.labelKey) : t('nav.menu')}</span>
        <Icon icon={ChevronDown} size={14} />
      </PopoverButton>
      <PopoverPanel className="k-navmenu__panel">
        {({ close }) => (
          <>
            <div className="k-navmenu__brand">
              <Logo className="k-navmenu__mark" size={24} />
              <span className="k-navmenu__word">
                <strong>{t('app.name')}</strong>
                <small>{t('app.tagline')}</small>
              </span>
            </div>
            {NAV.map((group, gi) => (
              <div className="k-navmenu__group" key={gi}>
                <span className="k-label k-navmenu__grouplabel">{t(group.labelKey)}</span>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={() => close()}
                    className={({ isActive }) =>
                      clsx('k-navmenu__item', isActive && 'k-navmenu__item--active')
                    }
                  >
                    <span className="k-navmenu__icon">
                      <Icon icon={item.icon} size={16} />
                    </span>
                    <span className="k-navmenu__text">{t(item.labelKey)}</span>
                    {item.badge === 'running' && status.activeSessions > 0 && (
                      <span className="k-navmenu__count">{status.activeSessions}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </>
        )}
      </PopoverPanel>
    </Popover>
  )
}

/* ----------------------------------------------------------------- TopBar */

function TopBar({
  connection,
  immersive,
  offer,
}: {
  connection: ConnectionState
  /** True when the band above has been dropped, which is when this row inherits
   *  the one fact it was carrying. */
  immersive: boolean
  /** The one install offer, shared with the sheet so the glyph here and the
   *  sheet below cannot disagree about whether there is anything to install. */
  offer: ReturnType<typeof useInstallOffer>
}) {
  const navigate = useNavigate()
  const t = useT()
  const { locale, setLocale } = useLocale()
  // Shared with the settings panel so the two theme controls never disagree.
  const [theme, setTheme] = useTheme()
  const [keys, setKeys] = useState(false)
  const fullscreen = useFullscreen()
  // Shared with the settings panel and the account menu, so no two of the three
  // can disagree about what the layout is currently doing.
  const [density] = useDensity()
  const tight = density === 'tight'
  // On a finger the five appearance controls below move into the account menu;
  // the reason is written where they are dropped, further down this file.
  const coarse = useCoarsePointer()
  // A device-wide choice, so it lives with the other device-wide ones rather
  // than in a terminal pane's header, where there would be one switch per
  // pane for one screen.
  const wake = useWakeLock()

  /**
   * F1 opens the reference, everywhere.
   *
   * It is the one keyboard convention older than the web — every desktop
   * environment has meant Help by it for thirty years — and this console spent
   * it on the command palette while the reference it describes had no key at
   * all. So the two swapped: the palette is on F2, beside it.
   *
   * Bound here rather than per screen because help is the one thing that must
   * answer from anywhere, including a screen with no palette and no terminal.
   * A pane swallows F1 before xterm can send it on (see TerminalPane), so this
   * still hears it with the keyboard inside a terminal — which is exactly when
   * an operator is most likely to want it.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'F1') return
      e.preventDefault()
      setKeys((open) => !open)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  /**
   * F3 goes to Dosyalar, from anywhere.
   *
   * On the F-row and never on Ctrl, because a terminal pane owns Ctrl — Ctrl+S
   * is XOFF and Ctrl+I is Tab — so a Ctrl chord would either be swallowed by
   * xterm or break the agent's own keys. Bound here rather than on the screen
   * itself for the reason F1 is: it has to answer from wherever the operator
   * happens to be, which is usually a terminal.
   *
   * The sheet lists it, and this is what stops that listing from being a key
   * that does not fire — the defect the Meta plane already has and is recorded
   * for.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'F3') return
      e.preventDefault()
      navigate('/files')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  return (
    <header className="k-topbar">
      <Link className="k-topbar__brand" to="/dashboard" aria-label={t('app.name')} title={t('app.name')}>
        <Logo className="k-topbar__mark" size={28} />
      </Link>
      <NavMenu />

      <div className="k-topbar__status">
        {/* The connection indicator is deliberately prominent. An operations
            console showing stale data as though it were live is worse than one
            that admits it lost the stream. */}
        <ConnectionIndicator state={connection} />

        <AttentionCluster />

        {/* The band's clock, when there is no band. Last in the status region
            rather than among the actions: it is something to read, and the
            actions are things to press. It gives way before the attention chips
            do — a UTC readout is worth a glance, and a session stopped for a
            person is worth an interruption. */}
        {immersive && <UtcClock className="k-topbar__clock" />}
      </div>

      <div className="k-topbar__actions">
        {/* Only while there is something to offer: a Chromium browser that has
            said it would install this page, or a device where installing is a
            share-sheet gesture the console can name and cannot press. Gone for
            good once installed, because an install button inside an installed
            app cannot do anything. See `useInstallApp` for the four states.

            It leads the cluster rather than joining the end of it: this is the
            one control here that is transient, and a transient control that
            appears between two permanent ones moves them under the pointer. */}
        {(offer.state === 'ready' || offer.offerable) && (
          <button
            type="button"
            className="k-topbar__btn k-topbar__btn--offer"
            /* On a finger, this opens the sheet instead of prompting: the sheet
               is what says *why* to install and, on iOS, where the control is —
               and on iOS there is nothing to prompt at all, so a button wired
               straight to `install()` would be a button that does nothing. On a
               pointer the browser is already offering this in its address bar,
               so the glyph stays the shortcut it has always been. */
            onClick={() => (offer.offerable ? offer.show() : void offer.install())}
            aria-label={t('topbar.installApp')}
            title={t('topbar.installAppHint')}
          >
            <Icon icon={MonitorDown} size={15} />
          </button>
        )}
        {/* The appearance controls — language, the keyboard reference, the
            layout switch, fullscreen and the theme — are a pointer's cluster. On
            a phone they are five 28px glyphs in a row that has already given up
            the section name and the account name for want of width, and none of
            them is something an operator reaches for mid-incident. They move
            whole into the account menu below, where each becomes a full-width
            row with a label; what stays in the bar is what needs a person and
            the way to the rest. */}
        {!coarse && (
          <button
            type="button"
            className="k-topbar__btn"
            onClick={() => setLocale(locale === 'tr' ? 'en' : 'tr')}
            aria-label={t('topbar.language')}
            title={t('topbar.language')}
          >
            {locale.toUpperCase()}
          </button>
        )}
        {/* Permanent, and on every screen, because it is the answer to a question
            an operator has exactly when they do not know where to look. Deliberately
            not a new global key: `?` inside a pane belongs to the shell running
            there, which is the rule that keeps both on the function row. */}
        {!coarse && (
          <button
            type="button"
            className="k-topbar__btn"
            onClick={() => setKeys(true)}
            aria-label={t('keys.title')}
            title={t('keys.title')}
          >
            <Icon icon={Keyboard} size={15} />
          </button>
        )}
        {/* The spreadsheet switch, next to fullscreen because the two answer the
            same request with the two halves of one screen: fullscreen takes the
            pixels the *browser* is spending, this takes the pixels the *console*
            is spending. Pressed, every frame gap closes, the rounding goes and
            neighbouring tiles share one border — a wall of terminals with no
            trough between them, and a table that reads like a spreadsheet.

            It writes a density rather than a mode of its own: there was already
            a three-value setting for how much room this interface takes, and a
            second control with its own boolean would have been two answers to
            one question, disagreeing the first time somebody used the other one.

            Deliberately not a keyboard shortcut. The function row is spoken for
            (F1 help, F2 palette, F3 files) and everything below it belongs to
            the shell inside a pane; a layout preference is not worth taking a
            key an agent's own editor is using. */}
        {!coarse && (
          <button
            type="button"
            className="k-topbar__btn"
            onClick={toggleTight}
            aria-pressed={tight}
            aria-label={tight ? t('topbar.tightExit') : t('topbar.tight')}
            title={tight ? t('topbar.tightHint') : t('topbar.tight')}
          >
            <Icon icon={tight ? Rows3 : Table} size={15} />
          </button>
        )}
        {/* Beside the theme switch because it is the same kind of control: how
            this console is displayed, not what it is showing. Left out entirely
            where the browser has no fullscreen — a button that cannot do the one
            thing it names is worse than an absence, and iOS Safari is the case.

            aria-pressed rather than two labels doing the work alone: this is a
            toggle whose state a screen reader has no other way to read, since
            the only visible difference is which glyph is drawn. */}
        {fullscreen.supported && !coarse && (
          <button
            type="button"
            className="k-topbar__btn"
            onClick={fullscreen.toggle}
            aria-pressed={fullscreen.full}
            aria-label={fullscreen.full ? t('topbar.fullscreenExit') : t('topbar.fullscreen')}
            title={fullscreen.full ? t('topbar.fullscreenExit') : t('topbar.fullscreen')}
          >
            <Icon icon={fullscreen.full ? Minimize : Maximize} size={15} />
          </button>
        )}
        {!coarse && (
          <button
            type="button"
            className="k-topbar__btn"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={t('topbar.theme')}
            title={t('topbar.theme')}
          >
            <Icon icon={theme === 'dark' ? Moon : Sun} size={15} />
          </button>
        )}
        <UserMenu
          extras={
            coarse
              ? {
                  onKeys: () => setKeys(true),
                  fullscreen: fullscreen.supported ? fullscreen : null,
                  wake: wake.supported ? wake : null,
                }
              : null
          }
        />
      </div>
      {keys && <ShortcutsOverlay onClose={() => setKeys(false)} />}
    </header>
  )
}

/* --------------------------------------------------------------- UserMenu */

interface AuthActor {
  username: string
  role: string
}

/**
 * The signed-in operator, top-right, with a dropdown for their session actions.
 * The identity is real — GET /auth/session — and the only action a control plane
 * this size needs here is signing out, which ends the current session server-side
 * and drops back to the sign-in gate.
 */
/**
 * What the account menu carries in addition to the account, on a phone.
 *
 * Passed in rather than read here, because both of these already live in the
 * top bar on a pointer and there must be one of each: two `useFullscreen`
 * calls would be two subscriptions to the same document event, and two `setKeys`
 * would be two overlays racing to be the one on screen.
 */
interface UserMenuExtras {
  onKeys: () => void
  /** Null where the browser has no fullscreen at all — iOS Safari is the case,
   *  and a row that cannot do the thing it names is worse than an absence. */
  fullscreen: { full: boolean; toggle: () => void } | null
  /** Null where the browser cannot hold a screen wake lock. Same rule. */
  wake: { wanted: boolean; toggle: () => void } | null
}

function UserMenu({ extras = null }: { extras?: UserMenuExtras | null }) {
  const t = useT()
  const { locale, setLocale } = useLocale()
  const [theme, setTheme] = useTheme()
  // Read here rather than passed in like fullscreen and the wake lock: this one
  // is an external store over an attribute on the document, so two readers are
  // two subscriptions to one value and cannot drift. Fullscreen is not — two
  // `useFullscreen` calls would be two subscriptions to the same document event.
  const [density] = useDensity()
  const session = useQuery({
    queryKey: ['auth-session'],
    queryFn: () => api.get<{ authenticated: boolean; actor?: AuthActor }>('/auth/session'),
    staleTime: 60_000,
  })
  const logout = useMutation({
    mutationFn: () => api.post<void>('/auth/logout', {}),
    // A full reload rather than a client redirect: signing out invalidates every
    // cached query at once, and re-running the gate from a clean slate is more
    // honest than patching the auth state in place.
    onSettled: () => window.location.assign('/'),
  })

  const actor = session.data?.actor
  const name = actor?.username ?? '—'
  const initial = (actor?.username ?? '?').charAt(0).toUpperCase()

  return (
    <Popover className="k-usermenu">
      <PopoverButton className="k-usermenu__button" aria-label={t('topbar.account')}>
        <span className="k-usermenu__avatar" aria-hidden>
          {initial}
        </span>
        <span className="k-usermenu__name">{name}</span>
        <Icon icon={ChevronDown} size={13} />
      </PopoverButton>
      <PopoverPanel className="k-usermenu__panel">
        {({ close }) => (
          <>
            <div className="k-usermenu__head">
              <span className="k-usermenu__avatar k-usermenu__avatar--lg" aria-hidden>
                {initial}
              </span>
              <span className="k-usermenu__id">
                <strong>{name}</strong>
                {actor?.role && <small className="k-mono">{actor.role}</small>}
              </span>
            </div>
            <NavLink to="/profile" className="k-usermenu__item" onClick={() => close()}>
              <Icon icon={UserRound} size={15} />
              <span>{t('nav.profile')}</span>
            </NavLink>
            <NavLink to="/settings" className="k-usermenu__item" onClick={() => close()}>
              <Icon icon={Settings} size={15} />
              <span>{t('nav.settings')}</span>
            </NavLink>
            {/* The appearance controls, on a phone. Each is a full-width row with
                a label rather than a 28px glyph, which is the whole reason they
                moved: the labels are what make a language toggle and a theme
                toggle tellable apart without pressing one. The menu stays open
                across them on purpose — changing the theme to see it is the
                normal way to use it, and a menu that closed on the first press
                would make the second a fresh journey. */}
            {extras && (
              <>
                <div className="k-usermenu__sep" role="separator" />
                <button
                  type="button"
                  className="k-usermenu__item"
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                >
                  <Icon icon={theme === 'dark' ? Moon : Sun} size={15} />
                  <span>{t('topbar.theme')}</span>
                </button>
                <button
                  type="button"
                  className="k-usermenu__item"
                  onClick={() => setLocale(locale === 'tr' ? 'en' : 'tr')}
                >
                  <Icon icon={Languages} size={15} />
                  <span>{t('topbar.language')}</span>
                  <span className="k-usermenu__value k-mono">{locale.toUpperCase()}</span>
                </button>
                {/* The same switch the top bar carries on a pointer, with the
                    label the 28px glyph could not fit. The menu stays open on
                    press for the reason the theme row does: what changes is
                    visible on this panel too — its own padding comes from the
                    scale being tightened — so pressing it is how you see it. */}
                <button
                  type="button"
                  className="k-usermenu__item"
                  aria-pressed={density === 'tight'}
                  onClick={() => toggleTight()}
                >
                  <Icon icon={density === 'tight' ? Rows3 : Table} size={15} />
                  <span>
                    {density === 'tight' ? t('topbar.tightExit') : t('topbar.tight')}
                  </span>
                </button>
                {extras.fullscreen && (
                  <button
                    type="button"
                    className="k-usermenu__item"
                    aria-pressed={extras.fullscreen.full}
                    onClick={() => {
                      close()
                      extras.fullscreen?.toggle()
                    }}
                  >
                    <Icon icon={extras.fullscreen.full ? Minimize : Maximize} size={15} />
                    <span>
                      {extras.fullscreen.full ? t('topbar.fullscreenExit') : t('topbar.fullscreen')}
                    </span>
                  </button>
                )}
                {/* Not closed on press, unlike fullscreen: this is a switch whose
                    whole value is that it stays on, and an operator needs to see
                    it take before they put the phone down beside a running
                    build. The menu is where they see it. */}
                {extras.wake && (
                  <button
                    type="button"
                    className="k-usermenu__item"
                    aria-pressed={extras.wake.wanted}
                    onClick={() => extras.wake?.toggle()}
                  >
                    <Icon icon={extras.wake.wanted ? Coffee : Moon} size={15} />
                    <span>{t('topbar.keepAwake')}</span>
                    <span className="k-usermenu__value k-mono">
                      {extras.wake.wanted ? t('topbar.keepAwakeOn') : t('topbar.keepAwakeOff')}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className="k-usermenu__item"
                  onClick={() => {
                    close()
                    extras.onKeys()
                  }}
                >
                  <Icon icon={Keyboard} size={15} />
                  <span>{t('keys.title')}</span>
                </button>
                <div className="k-usermenu__sep" role="separator" />
              </>
            )}
            <button
              type="button"
              className="k-usermenu__item k-usermenu__item--danger"
              onClick={() => {
                close()
                logout.mutate()
              }}
              disabled={logout.isPending}
            >
              <Icon icon={LogOut} size={15} />
              <span>{t('topbar.logout')}</span>
            </button>
          </>
        )}
      </PopoverPanel>
    </Popover>
  )
}

/**
 * What needs a person, and nothing else.
 *
 * It replaces two stats that reported the system's existence rather than any
 * call on the operator's time: a "system health" dot — a weaker restatement of
 * the risk list, which already names what is wrong — and a live count of active
 * sessions, which answers a question nobody was asking while three of them were
 * on screen. Both sat there permanently, and a chrome that is always lit teaches
 * an operator to stop reading it.
 *
 * So this is quiet by default and loud only when something is genuinely waiting.
 * Each item is a button, because a number an operator cannot act on is
 * decoration: the waiting count filters the session list to exactly those
 * sessions, and the risk count opens the screen that explains them.
 *
 * A failed dependency is named rather than coloured. `mongodb erişilemiyor` is
 * something to act on; an amber dot beside the word "health" is something to
 * ignore. The fact comes from the same risk detector the rail reads, which is
 * why deleting the bottom dependency bar lost nothing.
 */
function AttentionCluster() {
  const t = useT()
  const overview = useOverview()
  const { build, serverBuild } = useSystemStatus()
  const risks = overview.data?.risks ?? []
  // The server has been redeployed since this page loaded. See the note on
  // `build` in useSystemStatus: a single-page console never re-fetches its own
  // index.html, so this tab keeps running the bundle it was loaded with until
  // somebody reloads it — and nothing used to say so. A tab a day and a half
  // behind was read as the current interface.
  const staleBuild = build !== null && serverBuild !== null && build !== serverBuild

  const down = risks.filter((r) => r.kind === 'dependency_down')
  // Everything else, so a named dependency failure is not also counted as an
  // anonymous risk beside its own sentence.
  const others = risks.length - down.length
  // Both counts from the overview document rather than one from it and one from
  // the socket snapshot. It is the same number either way, but the overview is
  // the one that survives a dropped stream: the socket writes it when it is up
  // and a 15s poll refills it when it is not, so this cluster keeps counting
  // through exactly the failure that makes "is anything waiting for me" urgent.
  //
  // Every hop optional: this cluster is on every screen, so a payload without the
  // counter block — an older server, a degraded response — must leave the top bar
  // quiet rather than take the whole console down with it.
  const waiting = overview.data?.sessions?.waitingInput ?? 0

  return (
    <div className="k-attn">
      {/* "Nothing is waiting" is a claim, and it needs a measurement behind it.
          Until the overview has arrived there is no measurement — saying it
          anyway would be the console's own version of rendering an unknown as a
          confident zero, on the one line an operator glances at to decide
          whether they can look away. */}
      {overview.data !== undefined && waiting === 0 && risks.length === 0 && (
        <span className="k-attn__calm">
          <StatusDot tone="ok" label={t('attn.calm')} />
          <span className="k-attn__calmtext">{t('attn.calm')}</span>
        </span>
      )}
      <TightestQuota accounts={overview.data?.accounts ?? []} />
      {waiting > 0 && (
        <Link className="k-attn__item k-attn__item--wait" to="/live?waiting=1">
          <Icon icon={Hand} size={14} />
          <span>{t('attn.waiting', { count: waiting })}</span>
        </Link>
      )}
      {down.map((r) => (
        <Link
          key={r.kind + (r.params?.dependency ?? '')}
          className="k-attn__item k-attn__item--down"
          to="/dashboard"
          title={r.detail ?? r.title}
        >
          <Icon icon={PlugZap} size={14} />
          <span>{t('attn.depDown', { name: r.params?.dependency ?? r.title })}</span>
        </Link>
      ))}
      {others > 0 && (
        <Link className="k-attn__item k-attn__item--risk" to="/dashboard">
          <Icon icon={TriangleAlert} size={14} />
          <span>{t('attn.risks', { count: others })}</span>
        </Link>
      )}
      {/* A button, not a link: the act is reloading this document, and a link to
          the page you are already on would do nothing. It belongs in this
          cluster by the same rule as everything else here — it appears only when
          there is something to do, and it does it. */}
      {staleBuild && (
        <button
          type="button"
          className="k-attn__item k-attn__item--build"
          onClick={() => window.location.reload()}
          title={`${build} → ${serverBuild}`}
        >
          <Icon icon={RefreshCw} size={14} />
          <span>{t('attn.newBuild')}</span>
        </button>
      )}
    </div>
  )
}

/**
 * The subscription closest to running out, across the whole pool.
 *
 * The one number worth a permanent place in the chrome, because it is the one
 * that decides whether the next hour of work is possible at all — and because it
 * is a provider's own figure rather than anything Kimene invented (there are no
 * internal quotas; CLAUDE.md). It is what the "active sessions" counter here was
 * pretending to be: a fact you would change your plans over.
 *
 * Shown quietly while there is headroom and coloured as it tightens, so the bar
 * is never empty and never shouting. It leads to the accounts screen, where the
 * full per-window breakdown lives.
 */
function TightestQuota({ accounts }: { accounts: OverviewData['accounts'] }) {
  const t = useT()
  let worst: { label: string; window: UsageWindow } | undefined
  for (const a of accounts) {
    for (const w of a.usage?.windows ?? []) {
      if (!w.isActive) continue
      if (!worst || w.utilization > worst.window.utilization) worst = { label: a.label, window: w }
    }
  }
  // No account publishes usage. Unmeasured is not "plenty left", so nothing is
  // drawn rather than a confident zero.
  if (!worst) return null

  const pct = Math.round(worst.window.utilization)
  return (
    <Link
      className={clsx(
        'k-attn__item',
        'k-attn__item--quota',
        `k-attn__item--q${usageSeverityTone(worst.window.severity, worst.window.utilization)}`,
      )}
      to="/accounts"
      title={`${worst.label} · ${pct}%`}
    >
      <Icon icon={Gauge} size={14} />
      <span>{t('attn.quota', { pct: String(pct) })}</span>
    </Link>
  )
}

function ConnectionIndicator({ state }: { state: ConnectionState }) {
  const t = useT()
  if (state === 'open') {
    return (
      <span className="k-topbar__stat" title={t('topbar.live')}>
        <StatusDot tone="accent" live label={t('topbar.live')} />
      </span>
    )
  }
  if (state === 'reconnecting' || state === 'connecting') {
    return (
      <span className="k-topbar__stat k-topbar__stat--warn">
        <Spinner size={11} />
        <span className="k-topbar__stat-label">{t('terminal.reconnecting')}</span>
      </span>
    )
  }
  return (
    <span className="k-topbar__stat k-topbar__stat--err">
      <StatusDot tone="err" label={t('terminal.disconnected')} />
      <span className="k-topbar__stat-label">{t('terminal.disconnected')}</span>
    </span>
  )
}
